/* eslint-disable react/no-danger */
/* eslint-disable @typescript-eslint/no-misused-promises */

'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { trackEvent, getCommonEventProperties, isDesktop } from '@acko-tech/building-blocks.ui.acko-analytics'
import Image from 'next/image'
import { HEADER_THEME } from '@styles/theme'
import { logoutUser } from '@services/loginService'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import Cookies from 'js-cookie'
import {
  ACKO_LOGO_HORIZONTAL_GRADIENT_DARK,
  ACKO_LOGO_HORIZONTAL_GRADIENT_WHITE,
  ACKO_LOGO_HORIZONTAL_WHITE,
} from '@components/utils/ackoLogoUrls'
import { getClassNames } from '@helpers/productWidgetHelper'
import styles from './globalHeader.module.scss'
import Modal from './profile-modal/profile-modal'
import {
  KEY_REFRESH_TOKEN,
  KEY_USER_ID,
  SIGNUP_PATH,
  TAP_BTN_HEADER_HAMBURGER,
  TAP_BTN_SIGNUPNOW,
  TAP_HEADER_LOGIN_CTA,
  TAP_HEADER_MYACCOUNT,
  TAP_HEADER_RAISE_A_CLAIM_CTA,
} from './constants'
import { icons } from './icons'
import Button from './Button/Button'
import type IHeader from './interfaces/IHeader'
import { Categories } from './Categories'
import { ToggleIcon } from './ToggleIcon'
import { largeIcons } from './largeIcons'

interface IHeaderButton {
  setIsHeaderVisible: Function
  isHeaderVisible: boolean
  onClose: Function
  pageType?: any
  darkMobile: boolean
  blackVariant: boolean
  isHamburgerVisible: boolean
  theme: HEADER_THEME
  showRaiseAClaimInMobile: boolean
  onRaiseAClaimClick: () => void
}
interface IFAQButton {
  faqLink: string
  showLoginStatus: boolean
  faqIcon: string
  isMobile?: boolean
}
const ProfileModal = ({
  avatarClick,
  userData,
  avatarRef,
  largeHeader,
  showProfileModal,
  isHeaderVisible = false,
  handleMyPoliciesClicked,
  handleLogout,
}) => (
  <Modal
    handleIconClick={avatarClick}
    userData={userData}
    showModal={showProfileModal || isHeaderVisible}
    avatarRef={avatarRef}
    handleLogout={handleLogout}
    largeHeader={largeHeader}
    handleMyPoliciesClicked={handleMyPoliciesClicked}
  />
)

const FAQButton = (props: IFAQButton) => {
  const { faqLink, faqIcon, showLoginStatus, isMobile } = props
  if (!faqLink || faqLink.length === 0) {
    return null
  }
  return (
    <div
      className={getClassNames([], {
        [styles.faqSection]: !showLoginStatus,
      })}>
      <div
        className={`${styles.buttonWrapper} ${isMobile ? styles.forMobile : styles.forDesktop}`}
        onClick={() => window.open(faqLink, '_blank')}>
        <div
          className={`${styles.faqWrapper} ${!showLoginStatus ? styles.isSem : ''}  ${
            !isMobile ? styles.forDesktop : styles.forMobile
          }`}>
          <Image width={20} height={20} src={faqIcon} alt="FAQs section" />
          <span className={styles.faqText}>FAQs</span>
        </div>
      </div>
    </div>
  )
}
const HeaderButton = (props: IHeaderButton & IFAQButton) => {
  const {
    setIsHeaderVisible,
    isHeaderVisible,
    onClose,
    pageType,
    darkMobile,
    blackVariant,
    isHamburgerVisible,
    faqLink,
    faqIcon,
    showLoginStatus,
    isMobile = true,
    theme = HEADER_THEME.LIGHT,
    showRaiseAClaimInMobile,
    onRaiseAClaimClick,
  } = props
  const hamburgerClicked = e => {
    e.stopPropagation()
    e.preventDefault()
    if (isHeaderVisible) onClose()
    else {
      void trackEvent(TAP_BTN_HEADER_HAMBURGER, {
        ...getCommonEventProperties(pageType),
      })
    }
    setIsHeaderVisible((v: boolean) => !v)
  }
  return (
    <div className={styles.rightSideMobileActions}>
      {showRaiseAClaimInMobile && !isHeaderVisible && (
        <div className={`${styles.buttonWrapper} ${styles.raiseClaimMobile}`}>
          <Button
            isPrimary
            name="Raise a claim"
            onClick={onRaiseAClaimClick}
            outline={!darkMobile && theme === HEADER_THEME.LIGHT ? 'black' : 'white'}
          />
        </div>
      )}
      <FAQButton faqLink={faqLink} faqIcon={faqIcon} showLoginStatus={showLoginStatus} isMobile={isMobile} />
      {isHamburgerVisible && (
        <div className={getClassNames([styles.headerButton, styles[theme]], { [styles.buttonCross]: isHeaderVisible })}>
          <div
            onClick={e => hamburgerClicked(e)}
            className={getClassNames([styles.buttonVisuals, styles[theme]], {
              [styles.darkMobile]: darkMobile,
              [styles.black]: blackVariant,
            })}
          />
        </div>
      )}
    </div>
  )
}

const BackComponent = ({ white }) => (
  <span>
    {/* <LeftChevron stroke={white ? 'white' : ''} /> */}
    {icons.leftChevron({ stroke: white ? 'white' : '' })}
    <span className={white ? styles.backButtonWhite : styles.backButton}>Back</span>
  </span>
)

const HeaderVideo = ({ url, navOpen, Image }: { url: string; navOpen: boolean; Image: React.ReactNode }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    let playCount = 0

    if (!video) return
    const handleEnded = () => {
      playCount++
      if (playCount < 2) {
        video.currentTime = 0
        void video.play()
      }
    }

    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('ended', handleEnded)
    }
  }, [])
  return (
    <>
      <video
        className={getClassNames([styles.animatedLogo], {
          [styles.hide]: navOpen,
        })}
        ref={videoRef}
        autoPlay
        muted
        playsInline
        disableRemotePlayback
        disablePictureInPicture>
        <source src={url} type="video/mp4" />
      </video>
      {navOpen && Image}
    </>
  )
}

const GlobalHeader = (props: IHeader) => {
  useEffect(() => {
    function handleResize() {
      isDesktop() && setIsHeaderVisible(false)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const [isHeaderVisible, setIsHeaderVisible] = useState(false)
  const [currentlyActiveSection, setCurrentlyActiveSection] = useState(-1)
  const [currentlySelectedType, setCurrentlySelectedType] = useState({
    type: '',
    typeIndex: -1,
  })

  const [isShowingLinks, setIsShowingLinks] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false)
  const config = useEnvVariablesClientConfig()
  const {
    schema,
    darkDesktop = false,
    darkMobile = false,
    blackVariant = false,
    isWhiteVariant = false,
    logoVideoUrl,
    isLoggedIn,
    userData,
    largeHeader,
    pageType,
    theme = HEADER_THEME.DARK,
    isHomeWrapper,
    loginBaseUrl = '',
  } = props

  const AvatarClick = () => {
    void trackEvent(TAP_HEADER_MYACCOUNT, {
      ...getCommonEventProperties(pageType),
    }) // tap_header_profile_icon profile_icon_clicked
    setShowProfileModal(!showProfileModal)
  }

  const handleMyPoliciesClicked = useCallback(async () => {
    await trackEvent('tap_header_mypolicies_cta', {
      ...getCommonEventProperties(pageType),
    }).catch(() => null)
  }, [])

  const handleLogout = useCallback(async () => {
    try {
      await trackEvent('tap_header_logout_cta', {
        ...getCommonEventProperties(pageType),
      }).catch(() => null)

      await logoutUser({
        userId: Cookies.get(KEY_USER_ID),
        refreshToken: Cookies.get(KEY_REFRESH_TOKEN),
        env: config?.ACKO_ENV,
      })
    } catch (error) {
      console.error('Logout failed', error)
    } finally {
      Cookies.remove('refresh_token')
      Cookies.remove('user_id')
      window.location.reload()
    }
  }, [config])

  const avatarRef = useRef<any>()

  const LoginClicked = () => {
    void trackEvent(TAP_HEADER_LOGIN_CTA, {
      ...getCommonEventProperties(pageType),
    })
    window.location.href = loginBaseUrl
  }

  const SignupClicked = () => {
    void trackEvent(TAP_BTN_SIGNUPNOW, {
      ...getCommonEventProperties(pageType),
    })
    window.location.href = SIGNUP_PATH
  }

  const onRaiseAClaimClick = async () => {
    await trackEvent(TAP_HEADER_RAISE_A_CLAIM_CTA, {
      ...getCommonEventProperties(pageType),
    })

    if (isLoggedIn) {
      window.location.href = '/myaccount'
    } else {
      // Go to login and redirect to myaccount after successful login
      await trackEvent(TAP_HEADER_LOGIN_CTA, {
        ...getCommonEventProperties(pageType),
      })
      window.location.href = loginBaseUrl
    }
  }

  useEffect(() => {
    const rootElement = document.getElementById('__next')
    const bodyElement = document.body

    if (!isDesktop()) {
      if (isHeaderVisible) {
        rootElement && (rootElement.style['overflow-y'] = 'hidden')
        bodyElement && (bodyElement.style['overflow-y'] = 'hidden')
      } else {
        rootElement && (rootElement.style['overflow-y'] = 'scroll')
        bodyElement && (bodyElement.style['overflow-y'] = 'scroll')
      }
    }
  }, [isHeaderVisible])

  return (
    <>
      <header
        onMouseLeave={() => {
          setCurrentlyActiveSection(-1)
        }}
        id="global-header"
        className={getClassNames([styles.header], {
          [styles.isHeaderVisible]: isHeaderVisible,
          [styles.showingLinks]: isShowingLinks,
          [styles[theme]]: currentlyActiveSection === -1,
          [styles.activeSection]: currentlyActiveSection !== -1,
          [styles.darkMobile]: darkMobile,
          [styles.largeHeader]: !!largeHeader,
        })}>
        <div
          className={getClassNames([styles.headerMainWrapper], {
            [styles.isHeaderVisible]: isHeaderVisible,
            [styles.isLoggedIn]: !!isLoggedIn,
            [styles.darkDesktop]: darkDesktop,
            [styles.black]: blackVariant,
            [styles.largeHeader]: !!largeHeader,
            [styles.onlyFaqSection]: !schema?.showLoginStatus,
            [styles[theme]]: currentlyActiveSection === -1,
            [styles.isHomeWrapper]: !!isHomeWrapper,
          })}>
          <div
            className={getClassNames([styles.iconWrapper], {
              [styles.isHeaderVisible]: isHeaderVisible,
              [styles.largeHeader]: !!largeHeader,
              [styles.darkDesktop]: darkDesktop,
              [styles.darkMobile]: darkMobile,
              [styles.black]: blackVariant,
              [styles[theme]]: currentlyActiveSection === -1 && !isHeaderVisible,
            })}>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                if (!isShowingLinks || isDesktop()) {
                  window.location.href = '/?redirect=false'
                } else {
                  setCurrentlySelectedType({
                    type: '',
                    typeIndex: -1,
                  })
                  setCurrentlyActiveSection(-1)
                  setIsShowingLinks(false)
                }
              }}
              style={{
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              aria-label={isShowingLinks ? 'Back' : 'Home'}>
              {isShowingLinks && !isDesktop() ? (
                darkMobile ? (
                  <BackComponent white />
                ) : (
                  <BackComponent white={false} />
                )
              ) : logoVideoUrl ? (
                <HeaderVideo
                  url={logoVideoUrl}
                  navOpen={!(isHomeWrapper && currentlyActiveSection === -1 && !isHeaderVisible)}
                  Image={
                    <Image
                      width={100}
                      height={24}
                      src={blackVariant ? ACKO_LOGO_HORIZONTAL_WHITE : schema?.ackoCompanyLogo}
                      alt="Acko Company Logo"
                      priority
                    />
                  }
                />
              ) : (
                <Image
                  width={100}
                  height={24}
                  src={
                    isHomeWrapper && currentlyActiveSection === -1 && !isHeaderVisible
                      ? isWhiteVariant
                        ? ACKO_LOGO_HORIZONTAL_GRADIENT_DARK
                        : ACKO_LOGO_HORIZONTAL_GRADIENT_WHITE
                      : blackVariant
                        ? ACKO_LOGO_HORIZONTAL_WHITE
                        : schema?.ackoCompanyLogo
                  }
                  alt="Acko Company Logo"
                  priority
                />
              )}
            </button>

            <HeaderButton
              onClose={() => {
                setCurrentlyActiveSection(-1)
                // activateSection({section:-1})
                setCurrentlySelectedType({
                  type: '',
                  typeIndex: -1,
                })
                setIsShowingLinks(false)
              }}
              isHeaderVisible={isHeaderVisible}
              setIsHeaderVisible={setIsHeaderVisible}
              darkMobile={darkMobile || (theme === HEADER_THEME.DARK && !isHeaderVisible)}
              blackVariant={blackVariant}
              theme={theme}
              pageType={pageType}
              isHamburgerVisible={schema?.categories?.length > 0}
              faqLink={schema?.faqLink}
              showLoginStatus={schema?.showLoginStatus}
              faqIcon={schema?.faqIcon}
              showRaiseAClaimInMobile
              onRaiseAClaimClick={onRaiseAClaimClick}
            />
          </div>

          <Categories
            setIsShowingLinks={setIsShowingLinks}
            isShowingLinks={isShowingLinks}
            currentlySelectedType={currentlySelectedType}
            setCurrentlySelectedType={setCurrentlySelectedType}
            schema={schema}
            currentlyActiveSection={currentlyActiveSection}
            setCurrentlyActiveSection={setCurrentlyActiveSection}
            isHeaderVisible={isHeaderVisible}
            isLoggedIn={isLoggedIn}
            isDesktop={isDesktop()}
            pageType={pageType}
            largeHeader
            darkDesktop={darkDesktop}
            blackVariant={blackVariant}
            theme={theme}
            isHomeWrapper={isHomeWrapper}
            setShowProfileModal={setShowProfileModal}>
            {isLoggedIn && isHeaderVisible && (
              <div className={`${styles.mobileOnly}`}>
                <ProfileModal
                  avatarClick={AvatarClick}
                  userData={userData}
                  showProfileModal={showProfileModal}
                  avatarRef={avatarRef}
                  largeHeader={largeHeader}
                  isHeaderVisible={isHeaderVisible}
                  handleLogout={handleLogout}
                  handleMyPoliciesClicked={handleMyPoliciesClicked}
                />
              </div>
            )}
          </Categories>

          {schema?.showLoginStatus &&
            // <>
            (isLoggedIn ? (
              <div className={styles.accountGroupWrapper}>
                {!isHeaderVisible && (
                  <div className={styles.buttonWrapper}>
                    <Button
                      isPrimary
                      name="Raise a claim"
                      onClick={onRaiseAClaimClick}
                      outline={
                        currentlyActiveSection !== -1 && !darkDesktop
                          ? 'black'
                          : !darkDesktop && theme === HEADER_THEME.LIGHT
                            ? 'black'
                            : 'white'
                      }
                    />
                  </div>
                )}
                <div
                  className={getClassNames([styles.modalWrapper], {
                    [styles.darkDesktop]: darkDesktop,
                    [styles.black]: blackVariant,
                    [styles.active]: showProfileModal,
                    [styles[theme]]: currentlyActiveSection === -1,
                  })}
                  onClick={AvatarClick}>
                  <span className={`${styles.StyledImageContainer}`}>
                    {darkDesktop ? (
                      <Image src={largeIcons.avatarWhiteBackground} alt="avatar white" width={30} height={30} />
                    ) : (
                      <Image src={largeIcons.avatarGreyBackground} alt="avatar grey" width={30} height={30} />
                    )}
                  </span>
                  <span>My account</span>
                  <ToggleIcon
                    isPurple={
                      blackVariant || darkDesktop || (theme !== HEADER_THEME.LIGHT && currentlyActiveSection === -1)
                    }
                    isActive={showProfileModal}
                  />
                  <ProfileModal
                    avatarClick={AvatarClick}
                    userData={userData}
                    showProfileModal={showProfileModal}
                    avatarRef={avatarRef}
                    largeHeader={largeHeader}
                    handleMyPoliciesClicked={handleMyPoliciesClicked}
                    handleLogout={handleLogout}
                  />
                </div>
              </div>
            ) : isLoggedIn !== undefined ? (
              <div className={`${styles.buttonSection} ${schema?.faqLink?.length > 0 ? styles.hasFaq : ''}`}>
                {isDesktop() && (
                  <div className={`${styles.buttonWrapper}`} style={{ minWidth: '135px' }}>
                    <Button
                      isPrimary
                      name="Raise a claim"
                      onClick={() => onRaiseAClaimClick()}
                      outline={
                        currentlyActiveSection !== -1 && !darkDesktop
                          ? 'black'
                          : !darkDesktop && theme === HEADER_THEME.LIGHT
                            ? 'black'
                            : 'white'
                      }
                    />
                  </div>
                )}
                <div className={styles.buttonWrapper}>
                  <Button isPrimary name="Login" onClick={() => LoginClicked()} />
                </div>
                {isHeaderVisible && !isDesktop() && (
                  <div className={styles.buttonWrapper}>
                    <Button name="Sign Up" onClick={() => SignupClicked()} />
                  </div>
                )}
              </div>
            ) : null)}
          <FAQButton
            faqLink={schema?.faqLink}
            showLoginStatus={schema?.showLoginStatus}
            faqIcon={schema?.faqIcon}
            isMobile={false}
          />
        </div>
      </header>
      <div className={`${styles.relativeHeader} ${currentlyActiveSection !== -1 ? styles.visible : ''}`} />
    </>
  )
}

export default GlobalHeader
